import React, { useState, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import './HeistManagement.css';

export default function HeistManagement() {
  const { apiFetch } = useAuth();
  const { language } = useI18n();
  const [searchIdentifier, setSearchIdentifier] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [heistProfile, setHeistProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [xpAmount, setXpAmount] = useState('');
  const [creditsAmount, setCreditsAmount] = useState('');
  const [removeCreditsAmount, setRemoveCreditsAmount] = useState('');

  const t = useMemo(() => (language === 'da'
    ? {
        title: 'Heist Håndtering',
        subtitle: 'Administrer XP og kreditter for spillere',
        placeholder: 'Søg efter navn eller identifikator...',
        search: 'Søg',
        searching: 'Søger...',
        noPlayerSelected: 'Vælg en spiller for at administrere',
        giveXp: 'Giv XP',
        giveCredits: 'Giv Kreditter',
        removeCredits: 'Fjern Kreditter',
        xpAmount: 'XP Mængde',
        creditsAmount: 'Kreditmængde',
        identifier: 'Identifikator:',
        success: 'Succes',
        error: 'Fejl',
        enterValidAmount: 'Indtast en gyldig mængde',
        errorSearching: 'Fejl ved søgning efter spiller'
      }
    : {
        title: 'Heist Management',
        subtitle: 'Manage XP and credits for players',
        placeholder: 'Search by name or identifier...',
        search: 'Search',
        searching: 'Searching...',
        noPlayerSelected: 'Select a player to manage',
        giveXp: 'Give XP',
        giveCredits: 'Give Credits',
        removeCredits: 'Remove Credits',
        xpAmount: 'XP Amount',
        creditsAmount: 'Credits Amount',
        identifier: 'Identifier:',
        success: 'Success',
        error: 'Error',
        enterValidAmount: 'Enter a valid amount',
        errorSearching: 'Error searching for player'
      }), [language]);

  const fetchHeistProfile = useCallback(async (identifier) => {
    try {
      const response = await apiFetch('/api/heist/profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ identifier })
      });

      const data = await response.json();

      if (response.ok && data.profile) {
        setHeistProfile(data.profile);
      } else {
        setHeistProfile(null);
      }
    } catch (err) {
      console.error('Error fetching heist profile:', err);
      setHeistProfile(null);
    }
  }, [apiFetch]);

  const handleSearchPlayer = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!searchIdentifier.trim()) {
      setError(language === 'da' ? 'Indtast venligst et spillernavn' : 'Please enter a player name');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setSearchResults([]);

    try {
      const response = await apiFetch('/api/heist/player', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ identifier: searchIdentifier })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || t.errorSearching);
        setSelectedPlayer(null);
        setSearchResults([]);
      } else if (data.player) {
        // Single result (identifier search)
        setSelectedPlayer(data.player);
        setSearchResults([]);
        setXpAmount('');
        setCreditsAmount('');
        setRemoveCreditsAmount('');
        fetchHeistProfile(data.player.identifier);
      } else if (data.players && data.players.length > 0) {
        // Multiple results (name search)
        setSearchResults(data.players);
        setSelectedPlayer(null);
        setXpAmount('');
        setCreditsAmount('');
        setRemoveCreditsAmount('');
      }
    } catch (err) {
      setError(err.message || t.errorSearching);
    } finally {
      setLoading(false);
    }
  }, [searchIdentifier, apiFetch, language, t.errorSearching, fetchHeistProfile]);

  const selectPlayerFromResults = (player) => {
    setSelectedPlayer(player);
    setSearchResults([]);
    setXpAmount('');
    setCreditsAmount('');
    setRemoveCreditsAmount('');
    fetchHeistProfile(player.identifier);
  };

  

  const handleGiveXp = useCallback(async () => {
    if (!selectedPlayer || !xpAmount) return;

    const amount = parseInt(xpAmount, 10);
    if (isNaN(amount) || amount <= 0) {
      setError(t.enterValidAmount);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiFetch('/api/heist/give-xp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          identifier: selectedPlayer.identifier,
          amount
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(language === 'da' 
          ? `${amount} XP givet til ${selectedPlayer.identifier}` 
          : `${amount} XP given to ${selectedPlayer.identifier}`);
        setXpAmount('');
        fetchHeistProfile(selectedPlayer.identifier);
      } else {
        setError(data.error || (language === 'da' ? 'Fejl ved tildeling af XP' : 'Error giving XP'));
      }
    } catch (err) {
      setError(err.message || (language === 'da' ? 'Fejl ved tildeling af XP' : 'Error giving XP'));
    } finally {
      setLoading(false);
    }
  }, [selectedPlayer, xpAmount, apiFetch, language, t.enterValidAmount, fetchHeistProfile]);

  const handleGiveCredits = useCallback(async () => {
    if (!selectedPlayer || !creditsAmount) return;

    const amount = parseInt(creditsAmount, 10);
    if (isNaN(amount) || amount <= 0) {
      setError(t.enterValidAmount);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiFetch('/api/heist/give-credits', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          identifier: selectedPlayer.identifier,
          amount
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(language === 'da' 
          ? `${amount} kreditter givet til ${selectedPlayer.identifier}` 
          : `${amount} credits given to ${selectedPlayer.identifier}`);
        setCreditsAmount('');
        fetchHeistProfile(selectedPlayer.identifier);
      } else {
        setError(data.error || (language === 'da' ? 'Fejl ved tildeling af kreditter' : 'Error giving credits'));
      }
    } catch (err) {
      setError(err.message || (language === 'da' ? 'Fejl ved tildeling af kreditter' : 'Error giving credits'));
    } finally {
      setLoading(false);
    }
  }, [selectedPlayer, creditsAmount, apiFetch, language, t.enterValidAmount, fetchHeistProfile]);

  const handleRemoveCredits = useCallback(async () => {
    if (!selectedPlayer || !removeCreditsAmount) return;

    const amount = parseInt(removeCreditsAmount, 10);
    if (isNaN(amount) || amount <= 0) {
      setError(t.enterValidAmount);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiFetch('/api/heist/remove-credits', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          identifier: selectedPlayer.identifier,
          amount
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(language === 'da' 
          ? `${amount} kreditter fjernet fra ${selectedPlayer.identifier}` 
          : `${amount} credits removed from ${selectedPlayer.identifier}`);
        setRemoveCreditsAmount('');
        fetchHeistProfile(selectedPlayer.identifier);
      } else {
        setError(data.error || (language === 'da' ? 'Fejl ved fjernelse af kreditter' : 'Error removing credits'));
      }
    } catch (err) {
      setError(err.message || (language === 'da' ? 'Fejl ved fjernelse af kreditter' : 'Error removing credits'));
    } finally {
      setLoading(false);
    }
  }, [selectedPlayer, removeCreditsAmount, apiFetch, language, t.enterValidAmount, fetchHeistProfile]);

  return (
    <div className="heist-management">
      <div className="heist-header">
        <h2>{t.title}</h2>
        <p>{t.subtitle}</p>
      </div>

      <form className="heist-search-form" onSubmit={handleSearchPlayer}>
        <input
          type="text"
          className="heist-search-input"
          placeholder={t.placeholder}
          value={searchIdentifier}
          onChange={(e) => setSearchIdentifier(e.target.value)}
        />
        <button type="submit" className="heist-search-button" disabled={loading}>
          {loading ? t.searching : t.search}
        </button>
      </form>

      {error && <div className="heist-error-message">{error}</div>}
      {success && <div className="heist-success-message">{success}</div>}

      {searchResults.length > 0 && (
        <div className="heist-search-results">
          <div className="heist-results-header">
            <h3>{language === 'da' ? 'Søgeresultater' : 'Search Results'}</h3>
            <p>{language === 'da' ? `Fundet ${searchResults.length} resultater` : `Found ${searchResults.length} results`}</p>
          </div>
          <div className="heist-results-list">
            {searchResults.map((player) => (
              <button
                key={player.identifier}
                type="button"
                className="heist-result-item"
                onClick={() => selectPlayerFromResults(player)}
              >
                <div className="result-name">{player.fullName}</div>
                <div className="result-id">{player.identifier}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedPlayer ? (
        <div className="heist-layout">
          <div className="heist-sidebar">
            <div className="heist-sidebar-header">
              <h3>{language === 'da' ? 'Valgt Spiller' : 'Selected Player'}</h3>
            </div>
            <div className="heist-player-card">
              <div className="player-card-info">
                <strong>{selectedPlayer.identifier}</strong>
                <span className="player-name">{selectedPlayer.firstname} {selectedPlayer.lastname}</span>
              </div>
              {heistProfile && (
                <div className="heist-profile-stats">
                  <div className="stat-row">
                    <span className="stat-label">{language === 'da' ? 'XP:' : 'XP:'}</span>
                    <span className="stat-value">{heistProfile.exp}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">{language === 'da' ? 'Kreditter:' : 'Credits:'}</span>
                    <span className="stat-value">{heistProfile.credits}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="heist-content">
            <div className="heist-operations">
              <div className="heist-operation-card">
                <h4>{t.giveXp}</h4>
                <div className="operation-input-group">
                  <input
                    type="number"
                    placeholder={t.xpAmount}
                    value={xpAmount}
                    onChange={(e) => setXpAmount(e.target.value)}
                    min="1"
                    disabled={loading}
                  />
                  <button onClick={handleGiveXp} disabled={loading || !xpAmount} className="operation-button">
                    {t.giveXp}
                  </button>
                </div>
              </div>

              <div className="heist-operation-card">
                <h4>{t.giveCredits}</h4>
                <div className="operation-input-group">
                  <input
                    type="number"
                    placeholder={t.creditsAmount}
                    value={creditsAmount}
                    onChange={(e) => setCreditsAmount(e.target.value)}
                    min="1"
                    disabled={loading}
                  />
                  <button onClick={handleGiveCredits} disabled={loading || !creditsAmount} className="operation-button">
                    {t.giveCredits}
                  </button>
                </div>
              </div>

              <div className="heist-operation-card danger">
                <h4>{t.removeCredits}</h4>
                <div className="operation-input-group">
                  <input
                    type="number"
                    placeholder={t.creditsAmount}
                    value={removeCreditsAmount}
                    onChange={(e) => setRemoveCreditsAmount(e.target.value)}
                    min="1"
                    disabled={loading}
                  />
                  <button onClick={handleRemoveCredits} disabled={loading || !removeCreditsAmount} className="operation-button danger-button">
                    {t.removeCredits}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="heist-empty-state">
          <p>{t.noPlayerSelected}</p>
        </div>
      )}
    </div>
  );
}
